import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import * as JSZipNS from 'jszip';
import { FilesService } from 'src/app/service/files.service';
import { FileUploadErrors } from 'src/app/model/file';
import { Track } from 'src/app/model/track';
import { FileRules } from 'src/app/model/file.rules';
import { SubmissionStatus } from 'src/app/model/paper';
import { AdminService } from 'src/app/service/admin.service';
import { ExportService } from 'src/app/service/export.service';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { buildXlsxBlob } from 'src/app/utils/xlsx-export';
import { readXlsxSheet, XlsxReadError, XLSX_MAX_ROWS } from 'src/app/utils/xlsx-import';
import { isValidEmail, looksLikeEmail, normalizeHeader, pluralKey, stripWrappingQuotes } from 'src/app/utils/sheet-import-helpers';
import { EVENT_ADMIN_ICONS } from '../../event-admin-icons';

/** Situação de uma linha da planilha depois de lida e confrontada com a base. */
export enum SubmissionRowStatus {
  /** Autor encontrado, dados completos — pode ser registrada. */
  READY = 'ready',
  /** E-mail válido, mas sem conta no JEMS. Bloqueada: o chair cria a conta
   *  pela página de upload de usuários e reenvia esta planilha. */
  AUTHOR_NOT_FOUND = 'author-not-found',
  /** E-mail ausente ou malformado. */
  INVALID_EMAIL = 'invalid-email',
  /** Título/resumo ausente, acima do limite do banco, ou com cara de e-mail. */
  INCOMPLETE = 'incomplete',
  /** Mesmo autor + mesmo título em linha anterior da MESMA planilha. */
  DUPLICATE = 'duplicate',
  /** O evento já tem submissão desse autor com esse título (reenvio da
   *  planilha, ou criada por outra via). Mostra o ID. */
  EXISTING = 'existing',
  /** Já existe, mas em OUTRA trilha que não a escolhida na tela. Não é erro
   *  da planilha: é aviso de que o slot de arquivo daqui não serve para ela.
   *  Derivado (ver `displayStatus`), nunca gravado em `row.status` — depende
   *  da trilha selecionada, e o chair pode trocá-la a qualquer momento. */
  WRONG_TRACK = 'wrong-track',
  /** Registrada nesta sessão. Estado TERMINAL; mostra o ID. */
  CREATED = 'created',
}

export interface SubmissionRow {
  /** Número da linha na planilha (1 = cabeçalho). Também é a `key` do lookup. */
  sheetLine: number;
  /** E-mail como veio (aspas envolventes já removidas). */
  email: string;
  emailKey: string;
  title: string;
  abstract: string;
  /** Identificador CONTEXTUAL da planilha (`id NIC`) — externo ao JEMS, usado
   *  só durante este upload para casar o PDF com a submissão criada. Vazio
   *  quando a planilha não traz a coluna. */
  nic: string;
  /** Chave de duplicidade: autor + título normalizado. */
  dedupeKey: string;
  status: SubmissionRowStatus;
  selected: boolean;
  /** Nome da conta encontrada pelo e-mail. */
  authorName?: string;
  /** Autor achado por e-mail ALTERNATIVO, não pelo principal. */
  matchedBySecondary?: boolean;
  /** ID da submissão — a que já existia (`EXISTING`) ou a criada (`CREATED`). */
  submissionId?: number;
  /** Passou de pendente a ativa aqui mesmo, ao completar os obrigatórios. */
  activated?: boolean;
  /** Trilha da submissão JÁ EXISTENTE. O confronto varre o evento inteiro, em
   *  qualquer trilha, então ela pode não ser a escolhida na tela. Ausente nas
   *  linhas criadas aqui (essas nascem na trilha escolhida). */
  existingTrackId?: number;
  /** Motivo do problema da linha (chave i18n) — vale para `INCOMPLETE` e
   *  para `DUPLICATE`, que tem duas causas possíveis (autor+título ou id). */
  reasonKey?: string;
  creating?: boolean;
  createError?: string;
  /** PDF do ZIP que casou com o `id NIC` desta linha. */
  pdf?: File;
  /** Upload EM VOO desta linha. */
  attaching?: boolean;
  /** PDF já anexado nesta sessão. */
  attached?: boolean;
  /** Chave i18n da falha do anexo. */
  attachError?: string;
}

// Limites do model `Submission` no backend (title max_length=150,
// abstract max_length=3000). Validados aqui para a linha não prometer uma
// criação que o banco recusaria.
const TITLE_MAX = 150;
const ABSTRACT_MAX = 3000;

// Cabeçalho oficial do modelo baixável — o mesmo da planilha real que motivou
// a função (`Planilha Teste Submissões FIB17.xlsx`).
const TEMPLATE_HEADERS = ['E-mail - Contato', 'id NIC', 'Título', 'Resumo'];

// Interop CJS/ESM — mesma razão documentada em `xlsx-export.ts`.
const JSZip: any = (JSZipNS as any).default || JSZipNS;

const I18N = 'admin.event.submissions.submissions-upload';

@Component({
  selector: 'app-submissions-upload',
  templateUrl: './submissions-upload.component.html',
  styleUrls: ['./submissions-upload.component.scss']
})
export class SubmissionsUploadComponent implements OnInit, OnDestroy {
  readonly icons = EVENT_ADMIN_ICONS;
  readonly Status = SubmissionRowStatus;
  readonly maxRows = XLSX_MAX_ROWS;
  readonly i18n = I18N;

  fileName: string = null;
  rows: SubmissionRow[] = [];
  error: string = null;
  isLoading = false;
  isCreating = false;
  isDragging = false;
  /** Realce da zona de drop dos PDFs (separada da planilha). */
  isDraggingPdf = false;
  /** Nome do ZIP (ou "N arquivos" quando soltos) lido. */
  pdfSourceName: string = null;
  /** Lendo/descompactando o pacote. */
  isReadingPdfs = false;
  /** Anexando em lote. */
  isAttaching = false;
  /** Arquivos do pacote que não casaram com nenhum `id NIC` da tabela. */
  unmatchedFiles: string[] = [];
  /** Erro da leitura do pacote (chave i18n). */
  pdfError: string = null;
  /** Entradas do pacote já lidas. Guardadas porque o pacote pode chegar ANTES
   *  da planilha — aí o casamento acontece quando a tabela existir. */
  private pdfEntries: { name: string, file: File }[] = [];

  /** Trilha de destino (uma para o lote inteiro) e aviso aos autores. */
  readonly optionsForm: FormGroup;
  /** Opções do `app-jems-select`: `{ id, value }` a partir de `event.tracks`. */
  trackOptions: { id: number, value: string }[] = [];
  /** `true` depois que as trilhas carregaram — distingue "carregando" de
   *  "evento sem trilha nenhuma". */
  tracksLoaded = false;
  /** Trilhas do evento, com seus slots de arquivo (`trackFiles`). */
  private tracks: Track[] = [];
  /** Slots da trilha escolhida, no formato do `app-jems-select`. */
  trackFileOptions: { id: number, value: string }[] = [];

  /** Container PRÓPRIO das assinaturas. Não pode ser a Subscription devolvida
   *  pelo `getEvent()`: com o evento em cache ele é um `of(...)`, que completa
   *  na hora, e `add()` numa Subscription já FECHADA cancela a filha
   *  imediatamente — a troca de trilha deixava de disparar. */
  private readonly subs = new Subscription();

  constructor(
    private adminService: AdminService,
    private submissionsService: SubmissionsService,
    private exportService: ExportService,
    private filesService: FilesService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    fb: FormBuilder
  ) {
    // `notify` nasce DESLIGADO: e-mail para pessoas reais é escolha ativa do
    // chair, nunca efeito colateral de esquecer um checkbox marcado.
    this.optionsForm = fb.group({ track: [null], trackFile: [null], notify: [false] });
  }

  ngOnInit(): void {
    // O evento completo (`getEvent`) já traz `tracks` — não precisa de
    // chamada extra.
    this.subs.add(this.adminService.getEvent().subscribe(event => {
      this.tracks = event?.tracks || [];
      this.trackOptions = this.tracks
        .map(t => ({ id: t.id, value: t.name }))
        .sort((a, b) => a.value.localeCompare(b.value));
      // Uma trilha só: não há o que escolher.
      if (this.trackOptions.length === 1) {
        this.optionsForm.get('track').setValue(this.trackOptions[0].id);
      }
      this.refreshTrackFiles();
      this.tracksLoaded = true;
      this.cdr.markForCheck();
    }));

    // O slot pertence à trilha: trocar de trilha invalida a escolha anterior.
    this.subs.add(this.optionsForm.get('track').valueChanges
      .subscribe(() => { this.refreshTrackFiles(); this.cdr.markForCheck(); }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // Lê o CONTROLE, não `optionsForm.value`: o valor agregado do grupo só é
  // recalculado DEPOIS de o controle-filho emitir `valueChanges`, então
  // dentro do handler de troca de trilha o agregado ainda é o anterior — o
  // seletor de arquivo ficava um passo atrasado.
  get selectedTrack(): number { return this.optionsForm.get('track').value; }
  get selectedTrackFile(): number { return this.optionsForm.get('trackFile').value; }

  /**
   * Recalcula os slots de arquivo da trilha escolhida.
   *
   * Slots FECHADOS entram na lista, marcados. Importação em lote é
   * tipicamente retroativa — o prazo de envio já passou —, e o JEMS já trata
   * o chair como quem contorna prazo (`SubmissionView` aceita trilha fechada
   * quando quem cria é chair). Filtrar por prazo deixaria o seletor vazio
   * justo no caso de uso real: no evento de referência, TODOS os slots já
   * estavam encerrados. O backend já omite os que nem abriram.
   *
   * Com um slot só, escolhe sozinho; sem nenhum, a tela avisa.
   */
  private refreshTrackFiles(): void {
    const track = this.tracks.find(t => t.id === this.selectedTrack);
    const slots: FileRules[] = track?.trackFiles || [];
    this.trackFileOptions = slots.map(f => {
      const marks = [];
      if (!f.required) { marks.push(this.translate.instant(`${I18N}.slot-optional`)); }
      if (f.closed) { marks.push(this.translate.instant(`${I18N}.slot-closed`)); }
      return { id: f.id, value: marks.length ? `${f.name} (${marks.join(', ')})` : f.name };
    });

    const current = this.selectedTrackFile;
    const stillValid = this.trackFileOptions.some(o => o.id === current);
    if (!stillValid) {
      this.optionsForm.get('trackFile').setValue(
        this.trackFileOptions.length === 1 ? this.trackFileOptions[0].id : null, { emitEvent: false });
    }
  }

  /** Trilha escolhida, mas sem nenhum slot: não haverá onde pôr o PDF. */
  get trackHasNoFileSlot(): boolean {
    return !!this.selectedTrack && this.tracksLoaded && !this.trackFileOptions.length;
  }

  // ============================================================
  // Resumo
  // ============================================================

  get total(): number { return this.rows.length; }
  get readyCount(): number { return this.countOf(SubmissionRowStatus.READY); }
  get existingCount(): number { return this.countOf(SubmissionRowStatus.EXISTING); }
  get createdCount(): number { return this.countOf(SubmissionRowStatus.CREATED); }
  get blockedCount(): number { return this.countOf(SubmissionRowStatus.AUTHOR_NOT_FOUND); }
  get problemCount(): number {
    return this.countOf(SubmissionRowStatus.INVALID_EMAIL) + this.countOf(SubmissionRowStatus.INCOMPLETE)
      + this.countOf(SubmissionRowStatus.DUPLICATE);
  }

  /** `id NIC` → ID da submissão no JEMS, para o passo seguinte (anexar os
   *  PDFs pelo nome do arquivo). Vive só em memória, nesta página: o id é
   *  externo ao JEMS e o anexo acontece logo após o registro, na mesma tela —
   *  não há motivo pra gravar isso no banco.
   *
   *  Inclui as `EXISTING` de propósito: a submissão já existir não impede o
   *  PDF dela de ser anexado. */
  get nicToSubmissionId(): Map<string, number> {
    const map = new Map<string, number>();
    this.rows.forEach(r => {
      if (r.nic && r.submissionId) { map.set(r.nic.toLowerCase(), r.submissionId); }
    });
    return map;
  }

  /** Quantas linhas já têm par `id NIC` → submissão pronto pro anexo. */
  get linkableCount(): number { return this.nicToSubmissionId.size; }

  private countOf(status: SubmissionRowStatus): number {
    return this.rows.filter(r => r.status === status).length;
  }

  pluralKey(base: string, count: number): string {
    return pluralKey(base, count);
  }

  // ============================================================
  // Planilha modelo
  // ============================================================

  async downloadTemplate(): Promise<void> {
    const rows = [{ values: [
      'maria.andrade@exemplo.br',
      'a1b2',
      'Título da submissão (até 150 caracteres)',
      'Resumo da submissão.',
    ] }];
    const blob = await buildXlsxBlob('Submissões', TEMPLATE_HEADERS, rows, [3], [34, 10, 60, 90]);
    this.exportService.downloadBlob('jems-modelo-submissoes.xlsx', blob);
  }

  /**
   * Baixa a correspondência `id NIC` → ID da submissão como planilha.
   *
   * REDE DE SEGURANÇA, não a fonte de verdade: o vínculo é normalmente
   * reconstruído ao reenviar a MESMA planilha (as linhas já registradas
   * voltam do diagnóstico com o ID). Essa reconstrução casa por autor +
   * título normalizado, então ela se perde se o título for editado depois —
   * e é aí que este arquivo salva o dia.
   */
  async downloadNicMap(): Promise<void> {
    const rows = this.rows
      .filter(r => r.nic && r.submissionId)
      .map(r => ({ values: [r.nic, String(r.submissionId), r.email, r.title] }));
    if (!rows.length) { return; }
    const headers = ['id NIC', 'ID da submissão', 'E-mail - Contato', 'Título'];
    const blob = await buildXlsxBlob('Mapa', headers, rows, [], [10, 16, 34, 70], [], [], [1]);
    this.exportService.downloadBlob('jems-mapa-id-submissao.xlsx', blob);
  }

  // ============================================================
  // Upload
  // ============================================================

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const files = event.dataTransfer && event.dataTransfer.files;
    if (files && files.length) { this.handleFile(files[0]); }
  }

  onFileSelected(input: HTMLInputElement): void {
    if (input.files && input.files.length) { this.handleFile(input.files[0]); }
    // Zera o input pra reenviar o MESMO arquivo disparar `change` de novo.
    input.value = '';
  }

  clear(): void {
    this.fileName = null;
    this.rows = [];
    this.error = null;
    this.pdfEntries = [];
    this.pdfSourceName = null;
    this.unmatchedFiles = [];
    this.pdfError = null;
  }

  private async handleFile(file: File): Promise<void> {
    this.error = null;
    this.rows = [];
    this.fileName = file.name;
    this.isLoading = true;

    try {
      const sheet = await readXlsxSheet(file);
      // Local até o confronto terminar — a tabela só aparece pronta (mesma
      // razão do upload de autores: sem piscar num estado intermediário).
      const parsedRows = this.toRows(sheet);
      if (!parsedRows.length) {
        this.error = `${I18N}.errors.empty`;
        return;
      }
      await this.checkAuthorsAndExisting(parsedRows);
      parsedRows.filter(r => this.isSelectable(r)).forEach(r => r.selected = true);
      this.rows = parsedRows;
      // O pacote de PDFs pode ter sido solto ANTES da planilha.
      this.matchPdfs();
    } catch (e) {
      this.error = e instanceof XlsxReadError
        ? `${I18N}.errors.${e.code}`
        : `${I18N}.errors.read-failed`;
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  // ============================================================
  // Cabeçalho e linhas
  // ============================================================

  /**
   * Em que coluna está cada campo. Por palavra-chave, pra tolerar sufixos
   * ("E-mail - Contato") e acento/caixa (o cabeçalho já vem normalizado).
   * "resumo"/"abstract" e "titulo"/"title" cobrem planilha em PT ou EN.
   */
  private mapColumns(header: string[]): { email: number, title: number, abstract: number, nic: number } {
    const map = { email: -1, title: -1, abstract: -1, nic: -1 };
    header.forEach((cell, index) => {
      const h = normalizeHeader(cell);
      if (!h) { return; }
      if (map.email < 0 && h.indexOf('mail') >= 0) {
        map.email = index;
      } else if (map.abstract < 0 && (h.indexOf('resumo') >= 0 || h.indexOf('abstract') >= 0)) {
        map.abstract = index;
      } else if (map.title < 0 && (h.indexOf('titulo') >= 0 || h.indexOf('title') >= 0)) {
        map.title = index;
      } else if (map.nic < 0 && h.indexOf('nic') >= 0) {
        // Coluna OPCIONAL: identificador externo usado depois pra casar o PDF.
        // Sem ela a página funciona igual — só não dá pra anexar por id.
        map.nic = index;
      }
    });
    return map;
  }

  /** Espelha `normalize_title` do backend: lower + espaços colapsados. */
  private normalizeTitle(value: string): string {
    return (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  private toRows(sheet: string[][]): SubmissionRow[] {
    if (!sheet.length) { return []; }

    const columns = this.mapColumns(sheet[0]);
    if (columns.email < 0) { throw new XlsxReadError('no-email-column'); }
    if (columns.title < 0) { throw new XlsxReadError('no-title-column'); }

    const rows: SubmissionRow[] = [];
    const seen = new Set<string>();
    const seenNic = new Set<string>();
    // A coluna é opcional: só cobramos o id das linhas quando a planilha traz
    // a coluna. Sem ela, a página registra igual — só não haverá como casar
    // os PDFs por id depois.
    const hasNic = columns.nic >= 0;

    for (let i = 1; i < sheet.length; i++) {
      const cells = sheet[i];
      const at = (index: number) => (index >= 0 && cells[index] ? cells[index] : '');

      const email = stripWrappingQuotes(at(columns.email));
      const emailKey = email.toLowerCase();
      const title = at(columns.title).replace(/\s+/g, ' ');
      const abstract = at(columns.abstract);
      const nic = at(columns.nic);
      const row: SubmissionRow = {
        sheetLine: i + 1,
        email, emailKey, title, abstract, nic,
        dedupeKey: `${emailKey}|${this.normalizeTitle(title)}`,
        status: SubmissionRowStatus.READY,
        selected: false,
      };

      const incomplete = this.incompleteReason(title, abstract, hasNic, nic);
      if (!email || !isValidEmail(email)) {
        row.status = SubmissionRowStatus.INVALID_EMAIL;
      } else if (incomplete) {
        row.status = SubmissionRowStatus.INCOMPLETE;
        row.reasonKey = incomplete;
      } else if (hasNic && seenNic.has(nic.toLowerCase())) {
        // Id repetido torna AMBÍGUA a vinculação do PDF depois — bloqueia.
        row.status = SubmissionRowStatus.DUPLICATE;
        row.reasonKey = `${I18N}.duplicate-reason.nic`;
      } else if (seen.has(row.dedupeKey)) {
        // Duplicidade é autor + TÍTULO, não só e-mail: o mesmo autor pode
        // legitimamente ter várias submissões no lote.
        row.status = SubmissionRowStatus.DUPLICATE;
        row.reasonKey = `${I18N}.duplicate-reason.author-title`;
      } else {
        seen.add(row.dedupeKey);
        if (hasNic) { seenNic.add(nic.toLowerCase()); }
      }

      rows.push(row);
    }

    return rows;
  }

  /** Chave i18n do motivo, ou null se a linha está aceitável. */
  private incompleteReason(title: string, abstract: string, hasNic: boolean, nic: string): string {
    const base = `${I18N}.incomplete-reason.`;
    if (!title) { return base + 'missing-title'; }
    if (!abstract) { return base + 'missing-abstract'; }
    // Só exigido quando a planilha TEM a coluna: aí uma linha sem id ficaria
    // sem como receber o PDF.
    if (hasNic && !nic) { return base + 'missing-nic'; }
    if (title.length > TITLE_MAX) { return base + 'title-too-long'; }
    if (abstract.length > ABSTRACT_MAX) { return base + 'abstract-too-long'; }
    // Título com cara de e-mail: colunas deslocadas na planilha.
    if (looksLikeEmail(title)) { return base + 'title-looks-like-email'; }
    return null;
  }

  // ============================================================
  // Seleção
  // ============================================================

  /** Só `READY` é selecionável. `AUTHOR_NOT_FOUND` fica BLOQUEADA por decisão
   *  de produto: o chair resolve a conta pela página de upload de usuários. */
  isSelectable(row: SubmissionRow): boolean {
    return row.status === SubmissionRowStatus.READY;
  }

  /**
   * A submissão já existe, mas em outra trilha que não a escolhida na tela.
   *
   * DERIVADO, e não um `status` gravado, porque depende da trilha selecionada:
   * trocar o seletor reclassifica as linhas sozinho, sem refazer o lookup.
   */
  isWrongTrack(row: SubmissionRow): boolean {
    return !!row.existingTrackId && row.existingTrackId !== this.selectedTrack;
  }

  /** Status EXIBIDO na pílula — `status` cru, com a ressalva da trilha. */
  displayStatus(row: SubmissionRow): SubmissionRowStatus {
    return this.isWrongTrack(row) ? SubmissionRowStatus.WRONG_TRACK : row.status;
  }

  get selectableRows(): SubmissionRow[] { return this.rows.filter(r => this.isSelectable(r)); }
  get selectedRows(): SubmissionRow[] { return this.rows.filter(r => r.selected && this.isSelectable(r)); }
  get selectedCount(): number { return this.selectedRows.length; }

  allSelected(): boolean {
    const selectable = this.selectableRows;
    return selectable.length > 0 && selectable.every(r => r.selected);
  }

  someSelected(): boolean {
    const count = this.selectedCount;
    return count > 0 && count < this.selectableRows.length;
  }

  toggleAll(checked: boolean): void {
    this.selectableRows.forEach(r => r.selected = checked);
  }

  /** Motivo de o botão de registrar estar desabilitado — sem trilha é o caso
   *  que o chair não adivinha sozinho, então vira texto na barra. */
  get canCreate(): boolean {
    return !!this.selectedCount && !!this.selectedTrack && !this.isCreating;
  }

  // ============================================================
  // Registro
  // ============================================================

  /**
   * Registra as linhas marcadas — uma requisição por linha, em sequência,
   * cada resposta atualizando só a sua linha (mesmo desenho do upload de
   * autores). Nasce PENDENTE, um autor, sem arquivo, na trilha escolhida.
   *
   * Falha não para o lote. 409 (já existe) vira `EXISTING` com o ID
   * devolvido; 404 (autor sumiu desde a leitura) vira `AUTHOR_NOT_FOUND`;
   * o resto fica `READY` + marcada, e o botão tenta de novo só essas.
   */
  async createSelected(): Promise<void> {
    const toCreate = this.selectedRows;
    const track = this.selectedTrack;
    if (!toCreate.length || !track || this.isCreating) { return; }

    this.isCreating = true;
    const eventId = this.adminService.eventId;
    const notify = !!this.optionsForm.get('notify').value;

    for (const row of toCreate) {
      row.creating = true;
      row.createError = null;
      this.cdr.markForCheck();

      try {
        const created = await this.submissionsService.bulkCreate(eventId, {
          track, email: row.email, title: row.title, abstract: row.abstract, notify
        }).toPromise();
        row.status = SubmissionRowStatus.CREATED;
        row.submissionId = created.id;
        row.selected = false;
      } catch (e) {
        const params = e instanceof HttpErrorResponse ? e.error?.status?.params : null;
        if (e instanceof HttpErrorResponse && e.status === 409 && params?.submissionId) {
          row.status = SubmissionRowStatus.EXISTING;
          row.submissionId = params.submissionId;
          row.existingTrackId = params.trackId;
          row.selected = false;
        } else if (e instanceof HttpErrorResponse && e.status === 404) {
          row.status = SubmissionRowStatus.AUTHOR_NOT_FOUND;
          row.selected = false;
        } else {
          // O snackbar global (`ErrorService`) já avisa; aqui fica QUAL linha.
          row.createError = `${I18N}.errors.create-failed`;
        }
      } finally {
        row.creating = false;
        this.cdr.markForCheck();
      }
    }

    this.isCreating = false;
    this.cdr.markForCheck();
  }

  // ============================================================
  // PDFs (passo 2) — ZIP ou arquivos soltos, casados pelo `id NIC`
  // ============================================================

  /** O passo dos PDFs só faz sentido depois de haver linha com id e submissão. */
  get canAttachStep(): boolean { return this.linkableCount > 0; }
  get withPdfCount(): number { return this.rows.filter(r => !!r.pdf).length; }
  get attachedCount(): number { return this.rows.filter(r => r.attached).length; }
  /** Linhas prontas pra anexar: têm submissão, id, PDF e ainda não anexaram.
   *  Fora da trilha escolhida NÃO entra: o slot é daqui, a submissão é de
   *  outra trilha, e o backend rejeita (e antes gravava no lugar errado). */
  get attachableRows(): SubmissionRow[] {
    return this.rows.filter(r => r.submissionId && r.pdf && !r.attached && !this.isWrongTrack(r));
  }
  /** Linhas com submissão que ficaram SEM PDF no pacote. */
  get missingPdfCount(): number {
    return this.rows.filter(r => r.submissionId && r.nic && !r.pdf).length;
  }
  /** Quantas linhas esbarram na trilha — alimenta o aviso acima da tabela. */
  get wrongTrackCount(): number { return this.rows.filter(r => this.isWrongTrack(r)).length; }

  /** PDF casou com uma linha da planilha, mas ela não virou submissão (autor
   *  sem conta, dados incompletos...). O arquivo está certo; falta registrar. */
  get pdfWithoutSubmissionCount(): number {
    return this.rows.filter(r => r.pdf && !r.submissionId).length;
  }

  /**
   * O pacote de PDFs só é aceito DEPOIS da planilha.
   *
   * A caixa continua visível desde o começo — ela mostra que o passo existe —,
   * mas inerte: sem linhas lidas não há `id NIC` com que casar, e todo arquivo
   * cairia como "não reconhecido". Era o que confundia quem tentava começar
   * pelo zip.
   */
  get canReceivePdfs(): boolean { return this.rows.length > 0; }

  onPdfDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingPdf = this.canReceivePdfs;
  }
  onPdfDragLeave(event: DragEvent): void { event.preventDefault(); this.isDraggingPdf = false; }

  onPdfDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingPdf = false;
    if (!this.canReceivePdfs) { return; }
    const files = event.dataTransfer && event.dataTransfer.files;
    if (files && files.length) { this.handlePdfFiles(Array.from(files)); }
  }

  onPdfSelected(input: HTMLInputElement): void {
    if (this.canReceivePdfs && input.files && input.files.length) {
      this.handlePdfFiles(Array.from(input.files));
    }
    input.value = '';
  }

  /**
   * Aceita um .zip OU vários PDFs soltos e casa cada arquivo com a linha cujo
   * `id NIC` bate com o nome do arquivo (sem extensão, caixa ignorada).
   */
  private async handlePdfFiles(files: File[]): Promise<void> {
    this.pdfError = null;
    this.unmatchedFiles = [];
    this.rows.forEach(r => { r.pdf = null; r.attachError = null; });
    this.isReadingPdfs = true;

    try {
      const zip = files.find(f => /\.zip$/i.test(f.name));
      const entries = zip ? await this.readZip(zip) : files.map(f => ({ name: f.name, file: f }));
      this.pdfSourceName = zip ? zip.name : this.translate.instant(`${I18N}.pdf-loose-files`, { count: files.length });

      if (!entries.length) {
        this.pdfError = `${I18N}.errors.empty-package`;
        return;
      }

      // Casa com qualquer linha que tenha id — REGISTRADA OU NÃO. Um PDF cuja
      // linha existe mas não virou submissão não é "arquivo sem
      // correspondência" (que sugere nome errado); é um PDF à espera de uma
      // submissão que não foi criada, e a tela precisa dizer isso.
      this.pdfEntries = entries;
      this.matchPdfs();
    } catch {
      this.pdfError = `${I18N}.errors.zip-read-failed`;
    } finally {
      this.isReadingPdfs = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Descompacta o ZIP no navegador (jszip, já no bundle por causa do
   * xlsx-import/export).
   *
   * Ignora o que o macOS injeta ao compactar pelo Finder — a pasta
   * `__MACOSX/` e os arquivos AppleDouble `._nome.pdf` —, além de diretórios
   * e ocultos. Sem isso, cada PDF apareceria duas vezes e a segunda cópia
   * (lixo de metadados) poderia sobrescrever a boa.
   */
  private async readZip(file: File): Promise<{ name: string, file: File }[]> {
    const zip = await JSZip.loadAsync(file);
    const out: { name: string, file: File }[] = [];
    const paths: string[] = Object.keys(zip.files);

    for (const path of paths) {
      const entry = zip.files[path];
      if (entry.dir) { continue; }
      if (path.indexOf('__MACOSX/') === 0) { continue; }
      const base = path.split('/').pop() || '';
      if (!base || base.charAt(0) === '.') { continue; }
      const blob: Blob = await entry.async('blob');
      // O tipo NÃO vem do zip; sem ele a validação de mimetype do slot
      // rejeitaria todo arquivo. Deduzido da extensão.
      const type = /\.pdf$/i.test(base) ? 'application/pdf' : '';
      out.push({ name: base, file: new File([blob], base, { type }) });
    }
    return out;
  }

  /**
   * Casa as entradas do pacote com as linhas pelo `id NIC`.
   *
   * Roda nos DOIS sentidos — quando o pacote chega e quando a planilha chega —
   * porque a ordem é livre: dá pra soltar os PDFs antes de ler a planilha, e
   * a tabela já nasce mostrando quem tem arquivo esperando.
   */
  private matchPdfs(): void {
    this.unmatchedFiles = [];
    this.rows.forEach(r => { if (!r.attached) { r.pdf = null; } });
    if (!this.pdfEntries.length) { return; }

    const byNic = new Map<string, SubmissionRow>();
    this.rows.forEach(r => { if (r.nic) { byNic.set(r.nic.toLowerCase(), r); } });

    for (const entry of this.pdfEntries) {
      const key = this.baseNameWithoutExtension(entry.name).toLowerCase();
      const row = byNic.get(key);
      if (row) { row.pdf = entry.file; } else { this.unmatchedFiles.push(entry.name); }
    }
  }

  /**
   * Estado do PDF de uma linha — a coluna "PDF" da tabela conta essa história
   * por linha, em vez de um resumo separado embaixo.
   */
  pdfState(row: SubmissionRow): 'attaching' | 'attached' | 'error' | 'ready' | 'waiting'
    | 'missing' | 'blocked' | 'none' {
    if (row.attaching) { return 'attaching'; }
    if (row.attached) { return 'attached'; }
    if (row.attachError) { return 'error'; }
    // O PDF casou, mas a submissão mora em outra trilha: o slot escolhido
    // aqui não é dela, então não há para onde anexar a partir desta tela.
    if (row.pdf && this.isWrongTrack(row)) { return 'blocked'; }
    if (row.pdf) { return row.submissionId ? 'ready' : 'waiting'; }
    // Sem pacote carregado ainda não há o que dizer sobre a linha.
    if (!this.pdfSourceName || !row.nic) { return 'none'; }
    return 'missing';
  }

  /**
   * Promove a submissão de PENDENTE para ATIVA quando o anexo completa os
   * arquivos obrigatórios da trilha.
   *
   * O JEMS decide esse status no CLIENTE — o backend tem a promoção comentada
   * (`views/submission.py`, `status_mask = ACTIVE`) —, e hoje só três telas a
   * fazem: a página da submissão, o card da lista e o `file-rules-card`. Por
   * isso uma submissão criada aqui ficava pendente mesmo com PDF, e só virava
   * ativa quando alguém ABRIA a página dela. Usa `requiredFilesSubmitted`, a
   * mesma regra das outras três — nada de critério novo.
   *
   * Restrito às linhas CRIADAS nesta sessão, que nascem PENDING e cujo único
   * arquivo é o que acabamos de anexar. Linhas `EXISTING` ficam de fora de
   * propósito: não sabemos o status nem os demais arquivos delas, e um PUT
   * com ACTIVE sobre uma submissão já aceita/rejeitada apagaria esse estado.
   *
   * Falha em silêncio: o arquivo JÁ subiu, e derrubar o lote inteiro por causa
   * do status seria pior. Quem não promover aqui é promovido ao abrir a página,
   * como acontece hoje.
   */
  private async promoteIfComplete(row: SubmissionRow, slot: FileRules): Promise<void> {
    if (row.status !== SubmissionRowStatus.CREATED) { return; }
    const track = this.tracks.find(t => t.id === this.selectedTrack);
    const slots: FileRules[] = track?.trackFiles || [];
    if (!this.filesService.requiredFilesSubmitted(slots, [slot])) { return; }
    try {
      await this.submissionsService
        .editSubmission({ status: SubmissionStatus.ACTIVE }, row.submissionId).toPromise();
      row.activated = true;
    } catch {
      // Sem aviso na linha: não é falha do anexo, e a submissão continua
      // válida — apenas pendente até alguém abrir a página dela.
    }
  }

  private baseNameWithoutExtension(name: string): string {
    const base = (name || '').split('/').pop() || '';
    const dot = base.lastIndexOf('.');
    return dot > 0 ? base.slice(0, dot) : base;
  }

  /**
   * Anexa os PDFs casados, um por vez, cada um atualizando a sua linha.
   *
   * Reusa `filesService.uploadSubmissionFile` — o MESMO caminho do envio
   * manual —, então o arquivo entra com contagem de páginas, detecção de
   * template SBC e registro no histórico exatamente como qualquer outro.
   */
  async attachPdfs(): Promise<void> {
    const slot = this.currentSlot;
    const toAttach = this.attachableRows;
    if (!slot || !toAttach.length || this.isAttaching) { return; }

    this.isAttaching = true;
    for (const row of toAttach) {
      row.attaching = true;
      row.attachError = null;
      this.cdr.markForCheck();
      try {
        await this.filesService.uploadSubmissionFile(row.pdf, row.submissionId, slot).toPromise();
        row.attached = true;
        await this.promoteIfComplete(row, slot);
      } catch (e) {
        row.attachError = e === FileUploadErrors.MAX_SIZE ? `${I18N}.errors.file-too-big`
          : e === FileUploadErrors.INVALID_FORMAT ? `${I18N}.errors.file-wrong-type`
          : `${I18N}.errors.attach-failed`;
      } finally {
        row.attaching = false;
        this.cdr.markForCheck();
      }
    }
    this.isAttaching = false;
    this.cdr.markForCheck();
  }

  /** Nome do slot escolhido, pro rótulo do botão e pro gate da UI (null =
   *  não há onde anexar: trilha sem slot ou slot ainda não escolhido). */
  get currentSlotName(): string { return this.currentSlot ? this.currentSlot.name : null; }

  /** `FileRules` do slot escolhido — o upload precisa do objeto, não só do id. */
  private get currentSlot(): FileRules {
    const track = this.tracks.find(t => t.id === this.selectedTrack);
    return (track?.trackFiles || []).find(f => f.id === this.selectedTrackFile) || null;
  }

  // ============================================================
  // Confronto com a base
  // ============================================================

  /**
   * Uma ida só: quais autores têm conta, e quais linhas já existem como
   * submissão no evento. Só as linhas ainda `READY` vão — as demais já têm
   * problema local e não seriam registradas de qualquer jeito.
   */
  private checkAuthorsAndExisting(rows: SubmissionRow[]): Promise<void> {
    const pending = rows.filter(r => r.status === SubmissionRowStatus.READY);
    if (!pending.length) { return Promise.resolve(); }

    const eventId = this.adminService.eventId;
    const payload = pending.map(r => ({ key: r.sheetLine, email: r.emailKey, title: r.title }));

    return new Promise(resolve => {
      this.submissionsService.bulkLookup(eventId, payload).subscribe(result => {
        const authors = new Map((result.authors || []).map(a => [a.requestedEmail, a]));
        const existing = new Map((result.existing || []).map(x => [x.key, x]));

        pending.forEach(row => {
          const author = authors.get(row.emailKey);
          if (!author) {
            row.status = SubmissionRowStatus.AUTHOR_NOT_FOUND;
            return;
          }
          row.authorName = [author.firstName, author.lastName].filter(p => !!p).join(' ').trim() || null;
          row.matchedBySecondary = author.matchedField !== 'email';
          const found = existing.get(row.sheetLine);
          if (found) {
            row.status = SubmissionRowStatus.EXISTING;
            row.submissionId = found.submissionId;
            row.existingTrackId = found.trackId;
          }
        });

        resolve();
      }, () => {
        // Sem o confronto não dá pra afirmar que alguma linha é registrável:
        // bloqueia tudo em vez de prometer o que não foi verificado.
        this.error = `${I18N}.errors.lookup-failed`;
        pending.forEach(row => row.status = SubmissionRowStatus.AUTHOR_NOT_FOUND);
        resolve();
      });
    });
  }
}
